import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CalculationActions } from './calculation-action-types';
import { HttpErrorResponse } from '@angular/common/http';
import { Action } from '@ngrx/store';
import { catchError, map, Observable, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { CalculationsService } from '../../openapi';
import { selectCalculation } from './calculation.selectors';
import { Router } from '@angular/router';
import { LoggingService } from '../../services/logging.service';
import { IndexedDbService } from '../../services/indexeddb.service';
import { OpenedCalculationsService } from '../../services/opened-calculations.service';

@Injectable()
export class CalculationEffects {
  private readonly _router = inject(Router);
  private readonly _loggingService = inject(LoggingService);
  private readonly _store = inject(Store);
  private readonly _actions$ = inject(Actions);
  private readonly _calculationService = inject(CalculationsService);
  private readonly _indexedDbService = inject(IndexedDbService);
  private readonly _openedCalculationsService = inject(OpenedCalculationsService);

  loadCalculation$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CalculationActions.loadCalculation),
      switchMap(({ id }) =>
        this._calculationService.apiCalculationsIdGet(id).pipe(
          map((calculation) => CalculationActions.loadCalculationSuccess({ calculation })),
          catchError((error) => {
            this._redirectIfNeeded(error);
            return of(CalculationActions.loadCalculationFailure({ error: error.message }));
          })
        )
      )
    )
  );

  openCalculation$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CalculationActions.openCalculation),
      switchMap(({ id }) =>
        this._calculationService.apiCalculationsOpenIdPut(id).pipe(
          map((calculation) => CalculationActions.loadCalculationSuccess({ calculation })),
          catchError((error) => {
            this._redirectIfNeeded(error);
            return of(CalculationActions.loadCalculationFailure({ error: error.message }));
          })
        )
      )
    )
  );

  closeCalculation$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(CalculationActions.closeCalculation),
        withLatestFrom(this._store.select(selectCalculation)),
        switchMap(([action, calculation]) => {
          return this._calculationService.apiCalculationsCloseIdPut(calculation!.id!).pipe(
            tap(() => {
              calculation?.id && this._openedCalculationsService.remove(calculation.id);
              this._store.dispatch(CalculationActions.resetCalculationState());
              this._loggingService.success('Calculation Closed', 'You have successfully closed the Calculation');
              if (action.id) {
                this._router.navigate(['/calculations', action.id], { queryParams: { mode: 'edit' } });
              } else {
                this._router.navigate(['/']);
              }
            }),
            catchError(() => of())
          );
        })
      ),
    { dispatch: false }
  );

  saveCalculationSummary$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CalculationActions.saveCalculationSummary),
      withLatestFrom(this._store.select(selectCalculation)),
      switchMap(([action, calculation]) => {
        return this._calculationService.apiCalculationsSummaryIdPut(action.id, action.summary).pipe(
          map((saved) => CalculationActions.saveCalculationSummarySuccess({ summary: saved })),
          tap((a) => {
            this._notifySaved(a.summary, 'Calculation Summary successfully saved');

            this._indexedDbService.clear();

            if (action.redirectUrl) {
              this._router.navigateByUrl(action.redirectUrl);
            }
          }),
          catchError((error) => {
            return this._saveFailed(error, CalculationActions.saveCalculationSummaryFailure({ error: error.message }));
          })
        );
      })
    )
  );

  saveCalculationSettings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CalculationActions.saveCalculationSettings),
      withLatestFrom(this._store.select(selectCalculation)),
      switchMap(([action, calculation]) => {
        return this._calculationService.apiCalculationsSettingsIdPut(action.id, action.settings).pipe(
          map((saved) => CalculationActions.saveCalculationSettingsSuccess({ calculation: saved })),
          tap((a) => {
            this._notifySaved(a.calculation, 'Calculation Settings successfully saved');

            this._indexedDbService.clear();

            if (action.redirectUrl) {
              this._router.navigateByUrl(action.redirectUrl);
            }
          }),
          catchError((error) => {
            return this._saveFailed(error, CalculationActions.saveCalculationSettingsFailure({ error: error.message }));
          })
        );
      })
    )
  );

  saveCalculationEscalations$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CalculationActions.saveCalculationEscalations),
      withLatestFrom(this._store.select(selectCalculation)),
      switchMap(([action, calculation]) => {
        return this._calculationService
          .apiCalculationsEscalationsIdPut(action.id, action.escalations, action.close)
          .pipe(
            map((saved) => CalculationActions.saveCalculationEscalationsSuccess({ calculation: saved })),
            tap((a) => {
              this._notifySaved(a.calculation, 'Calculation Escalations successfully saved');

              this._indexedDbService.clear();

              if (action.redirectUrl) {
                this._router.navigateByUrl(action.redirectUrl);
              }
            }),
            catchError((error) => {
              return this._saveFailed(error, CalculationActions.saveCalculationEscalationsFailure({ error: error.message }));
            })
          );
      })
    )
  );

  saveCalculationDetailed$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CalculationActions.saveCalculationDetailed),
      withLatestFrom(this._store.select(selectCalculation)),
      switchMap(([action, calculation]) => {
        return this._calculationService.apiCalculationsDetailedIdPut(action.id, action.details).pipe(
          map((saved) => CalculationActions.saveCalculationDetailedSuccess({ calculation: saved })),
          tap((a) => {
            this._notifySaved(a.calculation, 'Calculation Detailed successfully saved');

            this._indexedDbService.clear();

            if (action.redirectUrl) {
              this._router.navigateByUrl(action.redirectUrl);
            }
          }),
          catchError((error) => {
            return this._saveFailed(error, CalculationActions.saveCalculationDetailedFailure({ error: error.message }));
          })
        );
      })
    )
  );

  saveCalculationFinancialCosts$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CalculationActions.saveCalculationFinancialCosts),
      withLatestFrom(this._store.select(selectCalculation)),
      switchMap(([action, calculation]) => {
        return this._calculationService.apiCalculationsFinancialcostsIdPut(action.id, action.costs).pipe(
          map((saved) => CalculationActions.saveCalculationFinancialCostsSuccess({ calculation: saved })),
          tap((a) => {
            this._notifySaved(a.calculation, 'Calculation Financial Costs successfully saved');

            this._indexedDbService.clear();

            if (action.redirectUrl) {
              this._router.navigateByUrl(action.redirectUrl);
            }
          }),
          catchError((error) => {
            return this._saveFailed(error, CalculationActions.saveCalculationFinancialCostsFailure({ error: error.message }));
          })
        );
      })
    )
  );

  saveCalculationCostItems$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CalculationActions.saveCalculationCostItems),
      switchMap((action) =>
        this._calculationService.apiCalculationsCostitemsIdPut(action.id, action.costItems).pipe(
          map((saved) => CalculationActions.saveCalculationCostItemsSuccess({ calculation: saved })),
          tap((a) => {
            this._notifySaved(a.calculation, 'Erection & Startup successfully saved');
            this._indexedDbService.clear();
            if (action.redirectUrl) {
              this._router.navigateByUrl(action.redirectUrl);
            }
          }),
          catchError((error) => this._saveFailed(error, CalculationActions.saveCalculationCostItemsFailure({ error: error.message })))
        )
      )
    )
  );

  saveCalculationExtrasDistribution$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CalculationActions.saveCalculationExtrasDistribution),
      switchMap((action) =>
        this._calculationService
          .apiCalculationsExtrasdistributionIdPut(action.id, action.extrasDistribution)
          .pipe(
            map((saved) => CalculationActions.saveCalculationExtrasDistributionSuccess({ calculation: saved })),
            tap((a) => {
              this._notifySaved(a.calculation, 'Extras Distribution successfully saved');
              this._indexedDbService.clear();
              if (action.redirectUrl) {
                this._router.navigateByUrl(action.redirectUrl);
              }
            }),
            catchError((error) =>
              this._saveFailed(error, CalculationActions.saveCalculationExtrasDistributionFailure({ error: error.message }))
            )
          )
      )
    )
  );

  saveCalculationMissingDpbs$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CalculationActions.saveCalculationMissingDpbs),
      switchMap((action) =>
        this._calculationService.apiCalculationsMissingdpbsIdPut(action.id, action.missingDpbs).pipe(
          map((saved) => CalculationActions.saveCalculationMissingDpbsSuccess({ calculation: saved })),
          tap((a) => {
            this._notifySaved(a.calculation, 'Missing DPBs successfully saved');
            this._indexedDbService.clear();
            if (action.redirectUrl) {
              this._router.navigateByUrl(action.redirectUrl);
            }
          }),
          catchError((error) =>
            this._saveFailed(error, CalculationActions.saveCalculationMissingDpbsFailure({ error: error.message }))
          )
        )
      )
    )
  );

  saveCalculationCustomerPrice$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CalculationActions.saveCalculationCustomerPrice),
      switchMap((action) =>
        this._calculationService.apiCalculationsCustomerpriceIdPut(action.id, action.customerPrice).pipe(
          map((saved) => CalculationActions.saveCalculationCustomerPriceSuccess({ calculation: saved })),
          tap((a) => {
            this._notifySaved(a.calculation, 'Customer Price successfully saved');
            this._indexedDbService.clear();
            if (action.redirectUrl) {
              this._router.navigateByUrl(action.redirectUrl);
            }
          }),
          catchError((error) =>
            this._saveFailed(error, CalculationActions.saveCalculationCustomerPriceFailure({ error: error.message }))
          )
        )
      )
    )
  );

  saveCalculationTotal$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CalculationActions.saveCalculationTotal),
      switchMap((action) =>
        this._calculationService.apiCalculationsTotalIdPut(action.id, action.total).pipe(
          map((saved) => CalculationActions.saveCalculationTotalSuccess({ calculation: saved })),
          tap((a) => {
            this._notifySaved(a.calculation, 'Total Calculation successfully saved');
            this._indexedDbService.clear();
            if (action.redirectUrl) {
              this._router.navigateByUrl(action.redirectUrl);
            }
          }),
          catchError((error) =>
            this._saveFailed(error, CalculationActions.saveCalculationTotalFailure({ error: error.message }))
          )
        )
      )
    )
  );

  /**
   * A save always answers 200 with the current calculation. When the caller no longer holds the
   * calculation open (someone else has it opened), the server discards the submitted changes and
   * returns the persisted, read-only state: `editMode !== 'edit'` while `openedById` is still set.
   * A save with `close = true` also comes back read-only, but then `openedById` is null — that one
   * is a real success. The store is replaced either way, so the page flips to view mode by itself.
   */
  private _notifySaved(saved: { editMode?: string | null; openedById?: string | null }, successMessage: string) {
    if (saved.editMode !== 'edit' && saved.openedById) {
      this._loggingService.warning(
        'Not saved',
        'The calculation is opened by another user and is read-only for you. Your changes were discarded and the saved state was reloaded.'
      );
      return;
    }

    this._loggingService.success('Save', successMessage);
  }

  /** Shows the server's problem detail as an error toast and emits the corresponding failure action. */
  private _saveFailed<T extends Action>(error: HttpErrorResponse, failureAction: T): Observable<T> {
    this._loggingService.error('Save', error);
    return of(failureAction);
  }

  private _redirectIfNeeded(error: any) {
    const route = this._getErrorRoute(error);
    if (route) {
      this._router.navigate([route]);
    }
  }

  private _getErrorRoute(error: any) {
    if (error.status === 401 || error.status === 403) return null; // authorization related errors will be caught by interceptor and redirection will be done from there

    return error.status === 404 ? '/404' : '/500';
  }
}
